import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import type { CellContext, ColumnDef } from '@tanstack/react-table'
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Filter,
  Pencil,
  Plus,
  Scissors,
  Search,
  Trash2,
  TrendingDown,
  TrendingUp,
  X,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { DataTable } from '@/components/DataTable'
import { Toolbar } from '@/components/Toolbar'
import { Modal } from '@/components/Modal'
import { Combobox, type ComboboxOption } from '@/components/Combobox'
import { StatCard } from '@/components/StatCard'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { FormField } from '@/components/FormField'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { formatCurrencyPrecise } from '@/lib/locale'
import { useReportBrand } from '@/lib/pdf'
import {
  useAccounts,
  useDeleteStatements,
  useRequestDeleteCode,
  useSplitStatement,
  useStatements,
  useUpdateStatement,
} from '@/features/viewStatements/queries'
import { usePostingStatus, usePostSelected } from '@/features/accruals/queries'
import { useUnpostedPreview } from '@/features/reporting/queries'
import { PostingBanner } from '@/components/PostingBanner'
import { PostingSelectorModal, type PostingSelectorItem } from '@/components/PostingSelectorModal'
import { formatDate } from '@/lib/locale'
import type {
  Statement,
  StatementsFilters,
  StatementsSort,
} from '@/features/viewStatements/types'
import { viewStatementsApi, type SplitLine } from '@/features/viewStatements/api'
import { useCoa } from '@/features/coa/queries'
import type { TaxonomyItem } from '@/features/coa/types'

const PAGE_SIZE = 50

const DEFAULT_FILTERS: StatementsFilters = {
  dateStart: '',
  dateEnd: '',
  details: '',
  amount: '',
  transactionType: '',
  category: '',
  account: '',
  status: '',
  uploadedStart: '',
  uploadedEnd: '',
  accountNo: '',
}

const DEFAULT_SORT: StatementsSort = { column: 'uploaded_at', direction: 'desc' }

type EditDraft = {
  category: string
  account_code: string
  subcategory: string
  statement_type: string
  main_category: string
}

function errorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function fmtMoney(v: number | string | null | undefined): string {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
  if (!isFinite(n)) return '-'
  return formatCurrencyPrecise(n)
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return '-'
  try {
    return new Date(v).toLocaleDateString()
  } catch {
    return String(v)
  }
}

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '-'
  try {
    return new Date(v).toLocaleString()
  } catch {
    return String(v)
  }
}

function statusBadge(status: string | null): 'secondary' | 'brand' | 'warning' {
  const s = (status || '').toLowerCase()
  // "completed" on a statement row is a data-state ("classification done"),
  // not a money-moving success event. Render in the tenant brand colour
  // so the page reads as on-brand and adapts to the dark theme cleanly,
  // instead of always showing a fixed-green pill.
  if (s === 'completed') return 'brand'
  if (s === 'pending') return 'warning'
  return 'secondary'
}

function useDebouncedValue<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

export function ViewStatementsPage() {
  const { toast } = useToast()

  // Sources of truth
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<StatementsSort>(DEFAULT_SORT)
  const [rawFilters, setRawFilters] = useState<StatementsFilters>(DEFAULT_FILTERS)
  const filters = useDebouncedValue(rawFilters, 400)

  // Selection (bulk delete)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  // Modals
  const [coaModal, setCoaModal] = useState<Statement | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const [deleteMode, setDeleteMode] = useState<null | 'selected' | 'account'>(null)
  const [deleteCode, setDeleteCode] = useState('')
  const [deleteErr, setDeleteErr] = useState<string | null>(null)
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false)

  const accounts = useAccounts()
  const q = useStatements({ page, limit: PAGE_SIZE, sort, filters })
  const requestCodeMut = useRequestDeleteCode()
  const deleteMut = useDeleteStatements()
  const updateStmtMut = useUpdateStatement()
  const splitStmtMut = useSplitStatement()
  const coaQuery = useCoa()

  // CoA lookups for inline edit + split modal.
  const coaMaps = useMemo(() => {
    const rows = coaQuery.data ?? []
    const byCode: Record<string, TaxonomyItem> = {}
    const codeOptions: ComboboxOption[] = []
    const subOptions: ComboboxOption[] = []
    for (const t of rows) {
      if (!t.account_code) continue
      byCode[t.account_code] = t
      codeOptions.push({
        value: t.account_code,
        label: t.account_code,
        description: `${t.subcategory_name} (${t.statement_type} › ${t.main_category})`,
      })
      subOptions.push({
        value: t.account_code,
        label: t.subcategory_name,
        description: `${t.statement_type} › ${t.main_category} · ${t.account_code}`,
      })
    }
    return { byCode, codeOptions, subOptions }
  }, [coaQuery.data])

  // Inline edit state
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editDraft, setEditDraft] = useState<EditDraft | null>(null)

  const startEdit = (row: Statement) => {
    setEditingId(row.id)
    setEditDraft({
      category: row.category ?? '',
      account_code: row.classified_account_code ?? '',
      subcategory: row.classified_subcategory ?? '',
      statement_type: (row as Statement & { classified_statement?: string }).classified_statement ?? '',
      main_category: row.classified_main_category ?? '',
    })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditDraft(null)
  }

  const patchEdit = (patch: Partial<EditDraft>) => {
    setEditDraft((d) => (d ? { ...d, ...patch } : d))
  }

  const pickReqIdRef = useRef(0)
  const pickEditCode = (code: string) => {
    const t = coaMaps.byCode[code]
    if (!t) {
      patchEdit({ account_code: code })
      return
    }
    patchEdit({
      account_code: t.account_code,
      subcategory: t.subcategory_name,
      statement_type: t.statement_type,
      main_category: t.main_category,
    })
    const reqId = ++pickReqIdRef.current
    void (async () => {
      try {
        const r = await viewStatementsApi.categoryLookup(t.account_code, t.subcategory_name)
        if (reqId !== pickReqIdRef.current) return
        if (r.category) patchEdit({ category: r.category })
      } catch {
        // No match in existing data - leave Type alone so user can enter it.
      }
    })()
  }

  const saveEdit = async () => {
    if (editingId === null || !editDraft) return
    try {
      await updateStmtMut.mutateAsync({
        id: editingId,
        payload: {
          category: editDraft.category || undefined,
          classified_account_code: editDraft.account_code || undefined,
          coa: {
            statement_type: editDraft.statement_type || undefined,
            main_category: editDraft.main_category || undefined,
            subcategory: editDraft.subcategory || undefined,
            account_code: editDraft.account_code || undefined,
          },
        },
      })
      toast({ title: 'Saved.' })
      cancelEdit()
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not save',
        description: errorMessage(err),
      })
    }
  }

  // Split modal state
  const [splitTarget, setSplitTarget] = useState<Statement | null>(null)

  // Reset selection + page when filters change
  useEffect(() => {
    setSelectedIds(new Set())
    setPage(1)
  }, [filters, sort.column, sort.direction])

  const rows = q.data?.data ?? []
  const pagination = q.data?.pagination
  const totalPages = pagination?.totalPages ?? 1
  const stats = {
    total: pagination?.totalRecords ?? 0,
    income: pagination?.totalIncome ?? 0,
    expenses: pagination?.totalExpenses ?? 0,
  }

  const toggleSelect = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const selectAllOnPage = (checked: boolean) => {
    if (!checked) {
      setSelectedIds(new Set())
      return
    }
    setSelectedIds(new Set(rows.map((r) => r.id)))
  }

  const clearFilters = () => setRawFilters(DEFAULT_FILTERS)

  const setFilter = <K extends keyof StatementsFilters>(key: K, value: StatementsFilters[K]) => {
    setRawFilters((prev) => ({ ...prev, [key]: value }))
  }

  const onSort = (column: string) => {
    setSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' },
    )
  }

  const beginDelete = async (mode: 'selected' | 'account') => {
    if (mode === 'selected' && selectedIds.size === 0) return
    try {
      await requestCodeMut.mutateAsync()
      setDeleteMode(mode)
      setDeleteCode('')
      setDeleteErr(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not send code',
        description: errorMessage(err),
      })
    }
  }

  const submitDelete = async () => {
    if (!deleteMode) return
    if (!deleteCode.trim()) {
      setDeleteErr('Please enter the verification code.')
      return
    }
    try {
      const result = await deleteMut.mutateAsync(
        deleteMode === 'selected'
          ? { verificationCode: deleteCode.trim(), transactionIds: Array.from(selectedIds) }
          : { verificationCode: deleteCode.trim(), accountNo: filters.accountNo || undefined },
      )
      toast({ title: 'Deleted', description: `Removed ${result.deletedCount} items.` })
      setDeleteMode(null)
      setSelectedIds(new Set())
    } catch (err) {
      setDeleteErr(errorMessage(err, 'Deletion failed.'))
    }
  }

  // Why: this page also shows the standard "Post Now" banner because
  // bank statement classifications land in the GL. The banner mirrors
  // the one on Accruals / Journal Entries / Schedules - same wording,
  // same selector modal - so the user has one consistent posting flow.
  const posting = usePostingStatus()
  const postSelectedMut = usePostSelected()
  const [postSelectorOpen, setPostSelectorOpen] = useState(false)
  const [selectorReadOnly, setSelectorReadOnly] = useState(false)
  const unpostedPreview = useUnpostedPreview(postSelectorOpen)
  const unpostedBs = posting.data?.unpostedBankStatements ?? 0

  const bsItems = useMemo<PostingSelectorItem[]>(() => {
    const rows = unpostedPreview.data?.bankStatements ?? []
    return rows.map((r) => ({
      id: r.id,
      cells: [
        formatDate(r.transactionDate),
        <span className="block max-w-[420px] truncate" title={r.description ?? undefined}>
          {r.description || '(no description)'}
        </span>,
        r.classifiedSubcategory || r.classifiedMainCategory || '-',
        fmtMoney(r.amount),
      ],
      blocked: r.blocked,
      blockedReason: r.blocked ? 'Closed period or pre-TB-cutoff' : undefined,
    }))
  }, [unpostedPreview.data])

  const bsColumns = [
    { header: 'Date', width: 'w-32' },
    { header: 'Description' },
    { header: 'Category' },
    { header: 'Amount', align: 'right' as const, width: 'w-28' },
  ]

  const runPostBs = async (selection: Record<string, number[]>) => {
    const ids = selection.bank ?? []
    if (ids.length === 0) return
    try {
      const r = await postSelectedMut.mutateAsync({ source: 'bank', ids })
      toast({
        title: 'Posted to GL',
        description: `Posted ${r.posted ?? 0} accounting entr${(r.posted ?? 0) === 1 ? 'y' : 'ies'} from ${ids.length} statement row(s).`,
      })
      setPostSelectorOpen(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Posting failed',
        description: errorMessage(err),
      })
    }
  }

  return (
    <Page
      title="Statements"
      description="Review imported transactions, fix classifications, export, or delete."
      width="wide"
      actions={
        <Toolbar>
          <Button variant="outline" size="sm" onClick={() => setExportOpen(true)}>
            <Download className="size-4" /> Download
          </Button>
          {selectedIds.size > 0 ? (
            <Button
              variant="danger"
              size="sm"
              onClick={() => beginDelete('selected')}
              disabled={requestCodeMut.isPending}
            >
              <Trash2 className="size-4" /> Delete ({selectedIds.size})
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => beginDelete('account')}
            disabled={requestCodeMut.isPending}
          >
            <Trash2 className="size-4" /> Delete{' '}
            {filters.accountNo ? `acct ${filters.accountNo}` : 'all data'}
          </Button>
        </Toolbar>
      }
    >
      <PostingBanner
        count={unpostedBs}
        label="bank statement entries"
        singularLabel="bank statement entry"
        source="bank"
        onView={() => { setSelectorReadOnly(true); setPostSelectorOpen(true) }}
        onPostNow={() => { setSelectorReadOnly(false); setPostSelectorOpen(true) }}
        busy={postSelectedMut.isPending}
      />

      <PostingSelectorModal
        open={postSelectorOpen}
        onOpenChange={setPostSelectorOpen}
        title={selectorReadOnly ? 'Pending bank statement entries' : 'Post bank statement entries'}
        description={selectorReadOnly
          ? 'These rows are queued to post. Click Post Now to choose which to post.'
          : 'Tick the rows you want to post. Posting only affects the rows you select - accruals, journal entries, and schedules are untouched.'}
        loading={unpostedPreview.isLoading}
        posting={postSelectedMut.isPending}
        readOnly={selectorReadOnly}
        onSwitchToPost={() => setSelectorReadOnly(false)}
        tabs={[
          {
            key: 'bank',
            label: 'Bank statements',
            items: bsItems,
            columns: bsColumns,
            emptyMessage: 'No bank statement rows are pending.',
          },
        ]}
        onPost={runPostBs}
      />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Total transactions" value={stats.total.toLocaleString()} icon={FileText} />
        <StatCard label="Total income" value={fmtMoney(stats.income)} icon={TrendingUp} />
        <StatCard label="Total expenses" value={fmtMoney(stats.expenses)} icon={TrendingDown} />
      </div>

      {/* Primary filter bar */}
      <Card>
        <CardContent className="space-y-3 pt-6">
          <Toolbar align="between">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              <Select
                value={filters.accountNo || 'ALL'}
                onValueChange={(v) => setFilter('accountNo', v === 'ALL' ? '' : v)}
              >
                <SelectTrigger className="h-10 w-auto min-w-[200px]">
                  <SelectValue placeholder="All accounts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All accounts</SelectItem>
                  {(accounts.data ?? []).map((a) =>
                    a.account_no ? (
                      <SelectItem key={a.account_no} value={a.account_no}>
                        Account ending in {a.account_no}
                      </SelectItem>
                    ) : null,
                  )}
                </SelectContent>
              </Select>

              <div className="relative min-w-[180px] flex-1">
                <Search
                  className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  value={rawFilters.details}
                  onChange={(e) => setFilter('details', e.currentTarget.value)}
                  placeholder="Search details…"
                  className="pl-9"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Filter className="size-4" /> More filters
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full max-w-sm">
                  <SheetHeader>
                    <SheetTitle>Filter transactions</SheetTitle>
                    <SheetDescription>
                      Narrow by date range, amount, category, or status.
                    </SheetDescription>
                  </SheetHeader>
                  <AdvancedFilters
                    filters={rawFilters}
                    onChange={setFilter}
                    onClear={clearFilters}
                  />
                </SheetContent>
              </Sheet>

              {hasActiveFilters(rawFilters) ? (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="size-4" /> Clear
                </Button>
              ) : null}
            </div>
          </Toolbar>

          {hasActiveFilters(rawFilters) ? (
            <ActiveFilterChips filters={rawFilters} onClear={(k) => setFilter(k, '')} />
          ) : null}
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent className="p-0">
          {q.isLoading ? (
            <div className="py-16">
              <LoadingSpinner />
            </div>
          ) : q.isError ? (
            <Alert variant="destructive" className="m-4">
              <AlertDescription>
                Could not load statements: {errorMessage(q.error)}
              </AlertDescription>
            </Alert>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No matching records"
              description={
                hasActiveFilters(filters)
                  ? 'Try clearing filters or selecting a different account.'
                  : 'Upload a bank statement to see transactions here.'
              }
            />
          ) : (
            <>
              {/* Desktop / tablet: DataTable */}
              <div className="hidden md:block">
                <StatementsTable
                  rows={rows}
                  selectedIds={selectedIds}
                  onToggle={toggleSelect}
                  onToggleAll={selectAllOnPage}
                  onRowCoaClick={setCoaModal}
                  sort={sort}
                  onSort={onSort}
                  editingId={editingId}
                  editDraft={editDraft}
                  onEditStart={startEdit}
                  onEditCancel={cancelEdit}
                  onEditSave={() => void saveEdit()}
                  onEditPatch={patchEdit}
                  onEditPickCode={pickEditCode}
                  onSplitStart={setSplitTarget}
                  coaCodeOptions={coaMaps.codeOptions}
                  coaSubOptions={coaMaps.subOptions}
                  onCoaOpen={() => void coaQuery.refetch()}
                  saving={updateStmtMut.isPending}
                />
              </div>

              {/* Mobile: card list */}
              <div className="divide-y md:hidden">
                {rows.map((row) => (
                  <StatementMobileCard
                    key={row.id}
                    row={row}
                    selected={selectedIds.has(row.id)}
                    onToggle={(v) => toggleSelect(row.id, v)}
                    onCoaClick={() => setCoaModal(row)}
                    editing={editingId === row.id}
                    editDraft={editingId === row.id ? editDraft : null}
                    onEditStart={() => startEdit(row)}
                    onEditCancel={cancelEdit}
                    onEditSave={() => void saveEdit()}
                    onEditPatch={patchEdit}
                    onEditPickCode={pickEditCode}
                    onSplitStart={() => setSplitTarget(row)}
                    coaCodeOptions={coaMaps.codeOptions}
                    coaSubOptions={coaMaps.subOptions}
                    onCoaOpen={() => void coaQuery.refetch()}
                    saving={updateStmtMut.isPending}
                  />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 ? (
        <Toolbar align="between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({stats.total.toLocaleString()} total)
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="size-4" /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next <ChevronRight className="size-4" />
            </Button>
          </div>
        </Toolbar>
      ) : null}

      {/* COA details (read-only) modal */}
      <Modal
        open={coaModal !== null}
        onOpenChange={(v) => !v && setCoaModal(null)}
        title="COA details"
        footer={
          <>
            <Button variant="secondary" asChild>
              <RouterLink to="/coa">Go to Chart of Accounts</RouterLink>
            </Button>
            <Button onClick={() => setCoaModal(null)}>Close</Button>
          </>
        }
      >
        {coaModal ? (
          <dl className="space-y-2 text-sm">
            <div className="flex items-start justify-between gap-3 border-b pb-2">
              <dt className="font-medium text-muted-foreground">COA code</dt>
              <dd className="font-mono font-semibold">
                {coaModal.classified_account_code || '-'}
              </dd>
            </div>
            <div className="flex items-start justify-between gap-3 border-b pb-2">
              <dt className="font-medium text-muted-foreground">Main category</dt>
              <dd>{coaModal.classified_main_category || '-'}</dd>
            </div>
            <div className="flex items-start justify-between gap-3">
              <dt className="font-medium text-muted-foreground">Subcategory</dt>
              <dd>{coaModal.classified_subcategory || '-'}</dd>
            </div>
          </dl>
        ) : null}
      </Modal>

      {/* Delete verify modal */}
      <Modal
        open={deleteMode !== null}
        onOpenChange={(v) => !v && setDeleteMode(null)}
        title="Confirm data deletion"
        description="Enter the 6-digit code we just emailed you."
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setDeleteMode(null)}
              disabled={deleteMut.isPending}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={submitDelete} disabled={deleteMut.isPending}>
              {deleteMut.isPending ? 'Deleting…' : 'Confirm delete'}
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <Input
            autoFocus
            maxLength={6}
            placeholder="123456"
            value={deleteCode}
            onChange={(e) => {
              setDeleteCode(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))
              setDeleteErr(null)
            }}
            className="text-center text-lg tracking-[0.4em]"
          />
          {deleteErr ? (
            <p className="text-sm text-destructive">{deleteErr}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {deleteMode === 'selected'
                ? `Will delete ${selectedIds.size} selected transaction${selectedIds.size === 1 ? '' : 's'}.`
                : filters.accountNo
                  ? `Will delete all transactions for account ${filters.accountNo}.`
                  : 'Will delete ALL transactions across every account.'}
            </p>
          )}
        </div>
      </Modal>

      {/* Export modal */}
      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        accountNo={filters.accountNo}
        initialDateFrom={filters.dateStart}
        initialDateTo={filters.dateEnd}
      />

      {/* Split transaction modal */}
      <SplitModal
        target={splitTarget}
        onClose={() => setSplitTarget(null)}
        coaByCode={coaMaps.byCode}
        coaSubOptions={coaMaps.subOptions}
        onSubmit={async (originalAmount, splits) => {
          if (!splitTarget) return
          try {
            await splitStmtMut.mutateAsync({
              id: splitTarget.id,
              payload: { originalAmount, splits },
            })
            toast({ title: 'Transaction split successfully.' })
            setSplitTarget(null)
          } catch (err) {
            toast({
              variant: 'destructive',
              title: 'Could not split',
              description: errorMessage(err),
            })
          }
        }}
        busy={splitStmtMut.isPending}
      />
    </Page>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────────

function hasActiveFilters(f: StatementsFilters): boolean {
  return Object.values(f).some((v) => v && v.length > 0)
}

const FILTER_LABELS: Record<keyof StatementsFilters, string> = {
  dateStart: 'From',
  dateEnd: 'To',
  details: 'Details',
  amount: 'Amount',
  transactionType: 'Transaction type',
  category: 'Category',
  account: 'Account field',
  status: 'Status',
  uploadedStart: 'Uploaded from',
  uploadedEnd: 'Uploaded to',
  accountNo: 'Bank account',
}

function ActiveFilterChips({
  filters,
  onClear,
}: {
  filters: StatementsFilters
  onClear: (key: keyof StatementsFilters) => void
}) {
  const entries = (Object.entries(filters) as [keyof StatementsFilters, string][]).filter(
    ([, v]) => v && v.length > 0,
  )
  if (entries.length === 0) return null
  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([key, value]) => (
        <Badge key={key} variant="secondary" className="gap-1">
          {FILTER_LABELS[key]}: {value}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onClear(key)}
            className="ml-0.5 size-4 rounded-pill p-0 hover:bg-muted"
            aria-label={`Clear ${FILTER_LABELS[key]} filter`}
          >
            <X className="size-3" />
          </Button>
        </Badge>
      ))}
    </div>
  )
}

function AdvancedFilters({
  filters,
  onChange,
  onClear,
}: {
  filters: StatementsFilters
  onChange: <K extends keyof StatementsFilters>(k: K, v: StatementsFilters[K]) => void
  onClear: () => void
}) {
  return (
    <div className="mt-4 space-y-4">
      <div>
        <h3 className="mb-2 text-sm font-semibold">Transaction date</h3>
        <div className="grid grid-cols-2 gap-2">
          <FormField
            label="From"
            type="date"
            value={filters.dateStart}
            onChange={(e) => onChange('dateStart', e.currentTarget.value)}
          />
          <FormField
            label="To"
            type="date"
            value={filters.dateEnd}
            onChange={(e) => onChange('dateEnd', e.currentTarget.value)}
          />
        </div>
      </div>
      <div>
        <h3 className="mb-2 text-sm font-semibold">Uploaded date</h3>
        <div className="grid grid-cols-2 gap-2">
          <FormField
            label="From"
            type="date"
            value={filters.uploadedStart}
            onChange={(e) => onChange('uploadedStart', e.currentTarget.value)}
          />
          <FormField
            label="To"
            type="date"
            value={filters.uploadedEnd}
            onChange={(e) => onChange('uploadedEnd', e.currentTarget.value)}
          />
        </div>
      </div>
      <FormField
        label="Amount"
        type="number"
        placeholder="e.g. 1500"
        value={filters.amount}
        onChange={(e) => onChange('amount', e.currentTarget.value)}
      />
      <FormField
        label="Transaction type"
        placeholder="e.g. Sales, Operating"
        value={filters.transactionType}
        onChange={(e) => onChange('transactionType', e.currentTarget.value)}
      />
      <FormField
        label="COA subcategory"
        placeholder="e.g. Consulting"
        value={filters.category}
        onChange={(e) => onChange('category', e.currentTarget.value)}
      />
      <FormField
        label="COA code"
        placeholder="e.g. 4001"
        value={filters.account}
        onChange={(e) => onChange('account', e.currentTarget.value)}
      />
      <div className="space-y-1.5">
        <label className="text-sm font-medium" htmlFor="vs-status">
          Status
        </label>
        <Select value={filters.status || 'ALL'} onValueChange={(v) => onChange('status', v === 'ALL' ? '' : v)}>
          <SelectTrigger id="vs-status">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All</SelectItem>
            <SelectItem value="created">Created</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button variant="ghost" onClick={onClear} className="w-full">
        Clear all filters
      </Button>
    </div>
  )
}

const SORTABLE_COLUMNS = new Set([
  'transaction_date',
  'transaction_details',
  'amount',
  'category',
  'classified_subcategory',
  'classified_account_code',
  'status',
  'uploaded_at',
])

type StatementsTableCtxValue = {
  selectedIds: Set<number>
  onToggle: (id: number, checked: boolean) => void
  onToggleAll: (checked: boolean) => void
  onRowCoaClick: (row: Statement) => void
  sort: StatementsSort
  onSort: (column: string) => void
  editingId: number | null
  editDraft: EditDraft | null
  onEditStart: (row: Statement) => void
  onEditCancel: () => void
  onEditSave: () => void
  onEditPatch: (patch: Partial<EditDraft>) => void
  onEditPickCode: (code: string) => void
  onSplitStart: (row: Statement) => void
  coaCodeOptions: ComboboxOption[]
  coaSubOptions: ComboboxOption[]
  onCoaOpen: () => void
  saving: boolean
  allOnPageSelected: boolean
}

const StatementsTableContext = createContext<StatementsTableCtxValue | null>(null)

function useStatementsCtx(): StatementsTableCtxValue {
  const ctx = useContext(StatementsTableContext)
  if (!ctx) throw new Error('StatementsTableContext is missing')
  return ctx
}

function SortHeader({ label, col }: { label: React.ReactNode; col: string }) {
  const { sort, onSort } = useStatementsCtx()
  const active = sort.column === col
  const sortable = SORTABLE_COLUMNS.has(col)
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => sortable && onSort(col)}
      className={cn(
        'h-auto gap-1 p-0 text-inherit hover:bg-transparent',
        !sortable && 'cursor-default',
      )}
    >
      {label}
      {active ? (
        <span className="text-xs">{sort.direction === 'asc' ? '▲' : '▼'}</span>
      ) : null}
    </Button>
  )
}

function SelectAllHeader() {
  const { allOnPageSelected, onToggleAll } = useStatementsCtx()
  return (
    <Checkbox
      checked={allOnPageSelected}
      onCheckedChange={(v) => onToggleAll(v === true)}
      aria-label="Select all rows on this page"
    />
  )
}

function SelectCell({ row }: CellContext<Statement, unknown>) {
  const { selectedIds, onToggle } = useStatementsCtx()
  return (
    <Checkbox
      checked={selectedIds.has(row.original.id)}
      onCheckedChange={(v) => onToggle(row.original.id, v === true)}
      aria-label={`Select transaction ${row.original.id}`}
    />
  )
}

function DateCell({ row }: CellContext<Statement, unknown>) {
  return <span className="whitespace-nowrap">{fmtDate(row.original.transaction_date)}</span>
}

function DetailsCell({ row }: CellContext<Statement, unknown>) {
  const { editingId, onSplitStart } = useStatementsCtx()
  const stmt = row.original
  const txt = stmt.transaction_details || '-'
  const disabled = editingId !== null
  const isSplitParent = stmt.is_split === true
  const isSplitChild = stmt.parent_transaction_id != null
  const splitNo = stmt.split_number ?? null
  return (
    <div className="flex items-center gap-1.5">
      {isSplitParent ? (
        <span
          title={`Split into ${splitNo ?? ''} line${splitNo === 1 ? '' : 's'}`}
          className="inline-flex items-center gap-1 rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary"
        >
          <Scissors className="size-3" />
          {splitNo ? `${splitNo} Split` : 'Split'}
        </span>
      ) : null}
      {isSplitChild ? (
        <span
          title="Child of a split transaction"
          className="inline-flex items-center text-xs font-semibold text-muted-foreground"
        >
          {splitNo ? `${splitNo}` : ''}
          <span aria-hidden className="ml-0.5">↳</span>
        </span>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => !disabled && onSplitStart(stmt)}
        disabled={disabled}
        title="Click to split this transaction"
        className={cn(
          'block h-auto max-w-[280px] truncate rounded-none px-0 py-0 text-left font-normal hover:bg-transparent',
          disabled
            ? 'cursor-not-allowed text-muted-foreground'
            : 'cursor-pointer text-foreground hover:text-primary hover:underline',
          isSplitChild && 'italic',
        )}
      >
        {txt}
      </Button>
    </div>
  )
}

function AmountCell({ row }: CellContext<Statement, unknown>) {
  return (
    <span
      className={cn(
        'block whitespace-nowrap text-right font-mono text-xs',
        parseFloat(String(row.original.amount)) < 0 ? 'text-destructive' : 'text-success',
      )}
    >
      {fmtMoney(row.original.amount)}
    </span>
  )
}

function CategoryCell({ row }: CellContext<Statement, unknown>) {
  const { editingId, editDraft, onEditPatch } = useStatementsCtx()
  if (editingId === row.original.id && editDraft) {
    return (
      <Input
        value={editDraft.category}
        onChange={(e) => onEditPatch({ category: e.currentTarget.value })}
        placeholder="Transaction type"
        className="h-8 min-w-[140px]"
      />
    )
  }
  return <>{row.original.category || '-'}</>
}

function SubcategoryCell({ row }: CellContext<Statement, unknown>) {
  const { editingId, editDraft, onEditPickCode, coaSubOptions, onCoaOpen } = useStatementsCtx()
  if (editingId === row.original.id && editDraft) {
    return (
      <div className="min-w-[220px]">
        <Combobox
          value={editDraft.account_code || null}
          onChange={(code) => code && onEditPickCode(String(code))}
          options={coaSubOptions}
          placeholder={editDraft.subcategory || 'Pick subcategory…'}
          triggerClassName="h-8"
          maxResults={10000}
          popoverClassName="w-[360px] max-w-[90vw]"
          onOpen={onCoaOpen}
        />
      </div>
    )
  }
  return (
    <span className="block max-w-[180px] truncate">
      {row.original.classified_subcategory || '-'}
    </span>
  )
}

function AccountCodeCell({ row }: CellContext<Statement, unknown>) {
  const {
    editingId,
    editDraft,
    onEditPickCode,
    coaCodeOptions,
    onCoaOpen,
    onRowCoaClick,
  } = useStatementsCtx()
  if (editingId === row.original.id && editDraft) {
    return (
      <div className="min-w-[180px]">
        <Combobox
          value={editDraft.account_code || null}
          onChange={(code) => code && onEditPickCode(String(code))}
          options={coaCodeOptions}
          placeholder="Pick code…"
          triggerClassName="h-8 font-mono"
          maxResults={10000}
          popoverClassName="w-[360px] max-w-[90vw]"
          onOpen={onCoaOpen}
        />
      </div>
    )
  }
  return row.original.classified_account_code ? (
    <Button
      variant="link"
      size="sm"
      className="h-auto p-0 font-mono text-xs"
      onClick={() => onRowCoaClick(row.original)}
    >
      {row.original.classified_account_code}
    </Button>
  ) : (
    <span>-</span>
  )
}

function StatusCell({ row }: CellContext<Statement, unknown>) {
  return <Badge variant={statusBadge(row.original.status)}>{row.original.status ?? '-'}</Badge>
}

function UploadedCell({ row }: CellContext<Statement, unknown>) {
  return (
    <span className="whitespace-nowrap text-xs text-muted-foreground">
      {fmtDateTime(row.original.uploaded_at)}
    </span>
  )
}

function ActionsCell({ row }: CellContext<Statement, unknown>) {
  const { editingId, saving, onEditSave, onEditCancel, onEditStart, onSplitStart } = useStatementsCtx()
  const editing = editingId === row.original.id
  if (editing) {
    return (
      <div className="flex justify-end gap-1">
        <Button
          variant="primary"
          size="icon"
          className="size-8"
          disabled={saving}
          onClick={onEditSave}
          aria-label="Save"
        >
          <Check className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          disabled={saving}
          onClick={onEditCancel}
          aria-label="Cancel"
        >
          <X className="size-4" />
        </Button>
      </div>
    )
  }
  return (
    <div className="flex justify-end gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="size-8"
        onClick={() => onEditStart(row.original)}
        aria-label="Edit"
        disabled={editingId !== null && editingId !== row.original.id}
      >
        <Pencil className="size-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-8"
        onClick={() => onSplitStart(row.original)}
        aria-label="Split"
        disabled={editingId !== null}
      >
        <Scissors className="size-4" />
      </Button>
    </div>
  )
}

const STATEMENTS_COLUMNS: ColumnDef<Statement, unknown>[] = [
  { id: 'select', header: SelectAllHeader, cell: SelectCell },
  { id: 'transaction_date', header: () => <SortHeader label="Date" col="transaction_date" />, cell: DateCell },
  { id: 'transaction_details', header: () => <SortHeader label="Details" col="transaction_details" />, cell: DetailsCell },
  { id: 'amount', header: () => <SortHeader label={<span className="block text-right">Amount</span>} col="amount" />, cell: AmountCell },
  { id: 'category', header: () => <SortHeader label="Type" col="category" />, cell: CategoryCell },
  { id: 'classified_subcategory', header: () => <SortHeader label="Subcategory" col="classified_subcategory" />, cell: SubcategoryCell },
  { id: 'classified_account_code', header: () => <SortHeader label="COA" col="classified_account_code" />, cell: AccountCodeCell },
  { id: 'status', header: () => <SortHeader label="Status" col="status" />, cell: StatusCell },
  { id: 'uploaded_at', header: () => <SortHeader label="Uploaded" col="uploaded_at" />, cell: UploadedCell },
  { id: 'actions', header: () => <span className="block text-right">Actions</span>, cell: ActionsCell },
]

function StatementsTable({
  rows,
  selectedIds,
  onToggle,
  onToggleAll,
  onRowCoaClick,
  sort,
  onSort,
  editingId,
  editDraft,
  onEditStart,
  onEditCancel,
  onEditSave,
  onEditPatch,
  onEditPickCode,
  onSplitStart,
  coaCodeOptions,
  coaSubOptions,
  onCoaOpen,
  saving,
}: {
  rows: Statement[]
  selectedIds: Set<number>
  onToggle: (id: number, checked: boolean) => void
  onToggleAll: (checked: boolean) => void
  onRowCoaClick: (row: Statement) => void
  sort: StatementsSort
  onSort: (column: string) => void
  editingId: number | null
  editDraft: EditDraft | null
  onEditStart: (row: Statement) => void
  onEditCancel: () => void
  onEditSave: () => void
  onEditPatch: (patch: Partial<EditDraft>) => void
  onEditPickCode: (code: string) => void
  onSplitStart: (row: Statement) => void
  coaCodeOptions: ComboboxOption[]
  coaSubOptions: ComboboxOption[]
  onCoaOpen: () => void
  saving: boolean
}) {
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id))

  const ctxValue = useMemo<StatementsTableCtxValue>(
    () => ({
      selectedIds,
      onToggle,
      onToggleAll,
      onRowCoaClick,
      sort,
      onSort,
      editingId,
      editDraft,
      onEditStart,
      onEditCancel,
      onEditSave,
      onEditPatch,
      onEditPickCode,
      onSplitStart,
      coaCodeOptions,
      coaSubOptions,
      onCoaOpen,
      saving,
      allOnPageSelected,
    }),
    [
      selectedIds,
      onToggle,
      onToggleAll,
      onRowCoaClick,
      sort,
      onSort,
      editingId,
      editDraft,
      onEditStart,
      onEditCancel,
      onEditSave,
      onEditPatch,
      onEditPickCode,
      onSplitStart,
      coaCodeOptions,
      coaSubOptions,
      onCoaOpen,
      saving,
      allOnPageSelected,
    ],
  )

  return (
    <StatementsTableContext.Provider value={ctxValue}>
      <DataTable sortable={false} rows={rows} columns={STATEMENTS_COLUMNS} />
    </StatementsTableContext.Provider>
  )
}

function StatementMobileCard({
  row,
  selected,
  onToggle,
  onCoaClick,
  editing,
  editDraft,
  onEditStart,
  onEditCancel,
  onEditSave,
  onEditPatch,
  onEditPickCode,
  onSplitStart,
  coaCodeOptions,
  coaSubOptions,
  onCoaOpen,
  saving,
}: {
  row: Statement
  selected: boolean
  onToggle: (checked: boolean) => void
  onCoaClick: () => void
  editing: boolean
  editDraft: EditDraft | null
  onEditStart: () => void
  onEditCancel: () => void
  onEditSave: () => void
  onEditPatch: (patch: Partial<EditDraft>) => void
  onEditPickCode: (code: string) => void
  onSplitStart: () => void
  coaCodeOptions: ComboboxOption[]
  coaSubOptions: ComboboxOption[]
  onCoaOpen: () => void
  saving: boolean
}) {
  const amount = parseFloat(String(row.amount ?? 0))
  return (
    <div className="flex items-start gap-3 p-4">
      <Checkbox
        checked={selected}
        onCheckedChange={(v) => onToggle(v === true)}
        aria-label={`Select transaction ${row.id}`}
        className="mt-1"
      />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => !editing && onSplitStart()}
            disabled={editing}
            title="Tap to split this transaction"
            className={cn(
              'h-auto truncate rounded-none px-0 py-0 text-left text-sm font-medium hover:bg-transparent',
              editing
                ? 'cursor-not-allowed text-muted-foreground'
                : 'cursor-pointer text-foreground hover:text-primary hover:underline',
            )}
          >
            {row.transaction_details || '-'}
          </Button>
          <span
            className={cn(
              'whitespace-nowrap font-mono text-sm font-semibold',
              amount < 0 ? 'text-destructive' : 'text-success',
            )}
          >
            {fmtMoney(row.amount)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>{fmtDate(row.transaction_date)}</span>
          {row.category && !editing ? <span>· {row.category}</span> : null}
          {row.classified_subcategory && !editing ? <span>· {row.classified_subcategory}</span> : null}
        </div>
        {editing && editDraft ? (
          <div className="mt-2 space-y-2">
            <Input
              value={editDraft.category}
              onChange={(e) => onEditPatch({ category: e.currentTarget.value })}
              placeholder="Transaction type"
              className="h-9"
            />
            <Combobox
              value={editDraft.account_code || null}
              onChange={(code) => code && onEditPickCode(String(code))}
              options={coaSubOptions}
              placeholder={editDraft.subcategory || 'Pick subcategory…'}
              triggerClassName="h-9"
              onOpen={onCoaOpen}
            />
            <Combobox
              value={editDraft.account_code || null}
              onChange={(code) => code && onEditPickCode(String(code))}
              options={coaCodeOptions}
              placeholder="Pick code…"
              triggerClassName="h-9 font-mono"
              onOpen={onCoaOpen}
            />
            <Toolbar>
              <Button variant="primary" size="sm" disabled={saving} onClick={onEditSave}>
                <Check className="size-4" /> Save
              </Button>
              <Button variant="ghost" size="sm" disabled={saving} onClick={onEditCancel}>
                <X className="size-4" /> Cancel
              </Button>
            </Toolbar>
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Badge variant={statusBadge(row.status)}>{row.status ?? '-'}</Badge>
          {row.classified_account_code && !editing ? (
            <Button variant="link" size="sm" className="h-auto p-0 font-mono text-xs" onClick={onCoaClick}>
              COA {row.classified_account_code}
            </Button>
          ) : null}
          {!editing ? (
            <>
              <Button variant="ghost" size="sm" onClick={onEditStart}>
                <Pencil className="size-4" /> Edit
              </Button>
              <Button variant="ghost" size="sm" onClick={onSplitStart}>
                <Scissors className="size-4" /> Split
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Export modal (lazy-loads jspdf)
// ──────────────────────────────────────────────────────────────────────────

function ExportModal({
  open,
  onClose,
  accountNo,
  initialDateFrom,
  initialDateTo,
}: {
  open: boolean
  onClose: () => void
  accountNo: string
  initialDateFrom: string
  initialDateTo: string
}) {
  const { toast } = useToast()
  const brand = useReportBrand()
  const [format, setFormat] = useState<'csv' | 'pdf'>('csv')
  const [dateFrom, setDateFrom] = useState(initialDateFrom)
  const [dateTo, setDateTo] = useState(initialDateTo)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (open) {
      setFormat('csv')
      setDateFrom(initialDateFrom)
      setDateTo(initialDateTo)
      setErr(null)
      setBusy(false)
    }
  }, [open, initialDateFrom, initialDateTo])

  const run = async () => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      setErr('"From" date must be on or before "To" date.')
      return
    }
    setErr(null)
    setBusy(true)
    try {
      const mod = await import('@/features/viewStatements/export')
      const count = await mod.runStatementsExport({ format, accountNo, dateFrom, dateTo, brand })
      toast({ title: `Downloaded ${count} transactions.` })
      onClose()
    } catch (e) {
      setErr(errorMessage(e, 'Failed to download data.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Download transactions"
      description="Choose a format and date range to export."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={run} disabled={busy}>
            {busy ? 'Preparing…' : 'Download'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
          Scope:{' '}
          <strong className="text-foreground">
            {accountNo ? `Account ending in ${accountNo}` : 'All accounts'}
          </strong>
        </div>
        <div>
          <p className="mb-2 text-sm font-semibold">Format</p>
          <div className="grid grid-cols-2 gap-2">
            {(['csv', 'pdf'] as const).map((f) => (
              <Button
                key={f}
                variant={format === f ? 'primary' : 'outline'}
                onClick={() => setFormat(f)}
              >
                {f.toUpperCase()}
              </Button>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-2 text-sm font-semibold">Date range</p>
          <div className="grid grid-cols-2 gap-2">
            <FormField
              label="From"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.currentTarget.value)}
            />
            <FormField
              label="To"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.currentTarget.value)}
            />
          </div>
        </div>
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
      </div>
    </Modal>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Split transaction modal
// ──────────────────────────────────────────────────────────────────────────

type SplitDraftLine = {
  description: string
  amount: string
  accountCode: string
  subcategory: string
}

function emptySplitLine(): SplitDraftLine {
  return { description: '', amount: '', accountCode: '', subcategory: '' }
}

function splitLineFromParent(parent: Statement | null): SplitDraftLine {
  if (!parent) return emptySplitLine()
  return {
    description: '',
    amount: '',
    accountCode: parent.classified_account_code ?? '',
    subcategory: parent.classified_subcategory ?? '',
  }
}

function SplitModal({
  target,
  onClose,
  coaByCode,
  coaSubOptions,
  onSubmit,
  busy,
}: {
  target: Statement | null
  onClose: () => void
  coaByCode: Record<string, TaxonomyItem>
  coaSubOptions: ComboboxOption[]
  onSubmit: (originalAmount: number, splits: SplitLine[]) => Promise<void> | void
  busy: boolean
}) {
  const [lines, setLines] = useState<SplitDraftLine[]>([splitLineFromParent(target)])
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (target) {
      setLines([splitLineFromParent(target)])
      setErr(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.id])

  if (!target) {
    return (
      <Modal open={false} onOpenChange={() => undefined} title="Split transaction">
        <span />
      </Modal>
    )
  }

  const originalAmount = parseFloat(String(target.amount ?? 0)) || 0
  const originalAbs = Math.abs(originalAmount)
  const isNegative = originalAmount < 0
  const totalSplitOff = lines.reduce((sum, l) => sum + (Math.abs(parseFloat(l.amount) || 0)), 0)
  const remaining = Math.round((originalAbs - totalSplitOff) * 100) / 100

  let balanceMessage: string
  let balanceTone: 'balanced' | 'unbalanced'
  let canSubmit = false
  if (totalSplitOff < 0.005) {
    balanceMessage = `Enter at least one split amount.`
    balanceTone = 'unbalanced'
  } else if (remaining > 0.004) {
    balanceMessage = `Original will keep: ${fmtMoney(remaining)} · Splitting off: ${fmtMoney(totalSplitOff)}`
    balanceTone = 'balanced'
    canSubmit = true
  } else if (Math.abs(remaining) < 0.005) {
    balanceMessage = `Entire amount split - original must keep some amount.`
    balanceTone = 'unbalanced'
  } else {
    balanceMessage = `Over by ${fmtMoney(Math.abs(remaining))} - reduce split amounts.`
    balanceTone = 'unbalanced'
  }

  const addLine = () => setLines((prev) => [...prev, splitLineFromParent(target)])
  const removeLine = (idx: number) =>
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)))
  const patch = (idx: number, patch: Partial<SplitDraftLine>) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)))
  const pickSubcategory = (idx: number, code: string) => {
    const t = coaByCode[code]
    if (!t) return patch(idx, { accountCode: code })
    patch(idx, {
      accountCode: t.account_code,
      subcategory: t.subcategory_name,
    })
  }

  const submit = async () => {
    if (!canSubmit) return
    setErr(null)
    const splits: SplitLine[] = []
    for (const l of lines) {
      const amt = parseFloat(l.amount)
      if (!amt) continue
      const signed = isNegative ? -Math.abs(amt) : Math.abs(amt)
      const tax = l.accountCode ? coaByCode[l.accountCode] : null
      splits.push({
        amount: signed,
        description: l.description.trim() || null,
        category: target.category ?? null,
        classified_account_code: l.accountCode || null,
        coa: tax
          ? {
              statement_type: tax.statement_type,
              main_category: tax.main_category,
              subcategory: tax.subcategory_name,
            }
          : l.subcategory
            ? { subcategory: l.subcategory }
            : null,
      })
    }
    if (splits.length === 0) {
      setErr('At least 1 split line with an amount is required.')
      return
    }
    const newOriginalAmt = isNegative
      ? originalAmount + totalSplitOff
      : originalAmount - totalSplitOff
    if (Math.abs(newOriginalAmt) < 0.005) {
      setErr('Cannot split the entire amount. The original must keep some amount.')
      return
    }
    await onSubmit(newOriginalAmt, splits)
  }

  return (
    <Modal
      open={true}
      onOpenChange={(v) => !v && onClose()}
      title="Split transaction"
      description={target.transaction_details ?? undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit || busy}>
            {busy ? 'Splitting…' : 'Split'}
          </Button>
        </>
      }
      className="sm:max-w-2xl"
    >
      <div className="space-y-4">
        <div className="rounded-md bg-muted/50 p-3 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              <strong>Original:</strong>{' '}
              <span className="font-mono">{fmtMoney(originalAmount)}</span>
            </span>
            <span className="text-xs text-muted-foreground">
              {fmtDate(target.transaction_date)}
            </span>
          </div>
        </div>

        <div className="space-y-3">
          {lines.map((line, idx) => (
            <div
              key={idx}
              className="rounded-md border p-3"
            >
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label className="text-xs font-medium text-muted-foreground">Description</label>
                  <Input
                    value={line.description}
                    onChange={(e) => patch(idx, { description: e.currentTarget.value })}
                    placeholder="Use original description if blank"
                    className="h-9"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="text-xs font-medium text-muted-foreground">Amount</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={line.amount}
                    onChange={(e) => patch(idx, { amount: e.currentTarget.value })}
                    placeholder="0.00"
                    className="h-9 font-mono"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-muted-foreground">Subcategory</label>
                  <Combobox
                    value={line.accountCode || null}
                    onChange={(code) => code && pickSubcategory(idx, String(code))}
                    options={coaSubOptions}
                    placeholder={line.subcategory || 'Pick…'}
                    triggerClassName="h-9"
                  />
                </div>
              </div>
              <div className="mt-2 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeLine(idx)}
                  disabled={lines.length <= 1}
                >
                  <X className="size-4" /> Remove
                </Button>
              </div>
            </div>
          ))}
        </div>

        <Button variant="outline" size="sm" onClick={addLine}>
          <Plus className="size-4" /> Add split line
        </Button>

        <div
          className={cn(
            'rounded-md px-3 py-2 text-sm',
            balanceTone === 'balanced'
              ? 'bg-success/10 text-success'
              : 'bg-destructive/10 text-destructive',
          )}
        >
          {balanceMessage}
        </div>

        {err ? <p className="text-sm text-destructive">{err}</p> : null}
      </div>
    </Modal>
  )
}
